import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/Dialog';
import { Button } from './ui/Button';
import { Label } from './ui/Label';
import { Input } from './ui/Input';
import { useState } from 'react';
import { useToast } from '~/hooks/use-toast';
import { useAuthStore } from '~/stores/useAuthStore';
import { useNavigate } from 'react-router';

interface AddTeamDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function AddTeamDialog({ open, setOpen }: AddTeamDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const createTeam = useAuthStore((state) => state.createTeam);
  const switchTeam = useAuthStore((state) => state.switchTeam);
  const refreshTeams = useAuthStore((state) => state.refreshTeams);
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData(event.currentTarget);
    const teamName = formData.get('team_name') as string;

    try {
      const newTeam = await createTeam({ name: teamName });

      const updatedTeams = await refreshTeams();

      switchTeam(newTeam.id, updatedTeams);

      toast({
        title: <span className="first-letter:capitalize">Success</span>,
        duration: 3000,
        description: 'Team created successfully',
      });

      setOpen(false);
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: <span className="first-letter:capitalize">Failed</span>,
        duration: 3000,
        variant: 'destructive',
        description:
          error instanceof Error ? error.message : 'Failed to create team',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
      }}
    >
      <DialogTrigger asChild className="w-full">
        <Button
          variant="outline"
          aria-label="Toggle Add Team"
          onClick={() => setOpen(true)}
        >
          + Add Team
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Team</DialogTitle>
          <DialogDescription>
            Create a new team to collaborate with others.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-2">
          <Label htmlFor="team_name">Name</Label>
          <Input
            id="team_name"
            name="team_name"
            type="text"
            placeholder="DViO"
            required
            minLength={3}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              aria-label="Add New Team"
              disabled={isLoading}
            >
              {isLoading ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
